import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiConfig';
import { supabase } from '../utils/supabase';
import MonthlyProgressSummary, { MonthlyProgressSummary as SummaryType } from '../app/member/components/MonthlyProgressSummary';
import { MetricRenderer } from '../app/member/components/MetricDisplayComponents';
import LogStaffProgressModal from '../components/LogStaffProgressModal';
import { ClipboardCheck, Activity, RotateCw, ArrowLeft, Copy } from 'lucide-react';
import toast from 'react-hot-toast';

import { useMemberAuth } from '../contexts/MemberAuthContext';

interface ReportListItem {
  id: string;
  reportMonth: string;
  fitnessScore: number;
  metricCount: number;
  metrics: Array<{
    metricValue: number | null;
    metricDefinition: {
      metricKey: string;
      displayName: string;
      metricCategory: string;
      displayType: string;
      metricUnit: string | null;
      improvementDirection: 'increase' | 'decrease';
    };
  }>;
}

interface MemberProgressGymPageProps {
  isMemberPage?: boolean;
}

const MemberProgressGymPage: React.FC<MemberProgressGymPageProps> = ({ isMemberPage = false }) => {
  const { memberId: routeMemberId } = useParams<{ memberId: string }>();
  const { member: authMember, token: memberToken } = useMemberAuth();
  
  const memberId = isMemberPage ? authMember?.id : routeMemberId;
  const navigate = useNavigate();
  const [latestSummary, setLatestSummary] = useState<SummaryType | null>(null);
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [memberName, setMemberName] = useState<string>('Member');
  const [memberPhoto, setMemberPhoto] = useState<string | null>(null);
  const [streak, setStreak] = useState(0);
  const [copying, setCopying] = useState(false);

  const lastFetchedRef = React.useRef<string | null>(null);

  const fetchData = React.useCallback(async () => {
    if (!memberId) return;
    
    // In member mode, wait for token to be available
    if (isMemberPage && !memberToken) return;

    // Deduplicate calls for same ID
    const fetchKey = `${memberId}_${isMemberPage ? 'member' : 'gym'}`;
    if (lastFetchedRef.current === fetchKey) return;
    lastFetchedRef.current = fetchKey;

    setLoading(true);
    try {
      let authHeader = '';
      let apiPath = '';
      
      if (isMemberPage) {
        authHeader = `Bearer ${memberToken}`;
        apiPath = `/app/member/progress/all`;
      } else {
        const { data: { session } } = await supabase.auth.getSession();
        authHeader = `Bearer ${session?.access_token}`;
        apiPath = `/api/progress/${memberId}`;
      }

      const res = await fetch(`${API_BASE_URL}${apiPath}`, {
        headers: {
          'Authorization': authHeader
        }
      });
      const result = await res.json();
      
      if (res.ok && result.success) {
        const data = result.data;
        setLatestSummary(data.latestSummary);
        setReports(data.reports || []);
        setStreak(data.streakMonths || 0);
        
        if (data.healthProfile?.fullName) {
          setMemberName(data.healthProfile.fullName);
        } else if (data.healthProfile?.displayName) {
          setMemberName(data.healthProfile.displayName);
        }
        
        if (data.profilePhoto) {
          setMemberPhoto(data.profilePhoto);
        }
      } else if (res.status === 403) {
        setError(isMemberPage ? 'Failed to load progress data.' : 'Member has not shared their progress with the gym.');
      } else {
        setError(result.error || 'Failed to load progress data');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Connection error. Please try again.');
      lastFetchedRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [memberId, memberToken, isMemberPage]);

  const handleCopyFromPrevious = async () => {
    if (copying) return;
    setCopying(true);
    try {
      let authHeader = '';
      let path = '';
      if (isMemberPage) {
        authHeader = `Bearer ${memberToken}`;
        path = `/app/member/progress/reports/previous-metrics`;
      } else {
        const { data: { session } } = await supabase.auth.getSession();
        authHeader = `Bearer ${session?.access_token}`;
        path = `/api/progress/${memberId}/previous-metrics`;
      }

      const res = await fetch(`${API_BASE_URL}${path}`, {
        headers: { Authorization: authHeader }
      });
      const result = await res.json();

      if (!result.success || !result.data?.previousReport) {
        toast('No previous month data to copy from.', { icon: 'ℹ️' });
        return;
      }

      // Open the log modal — it will auto-load the current month.
      // The Copy from Previous inside the modal handles the actual pre-fill.
      // We just surface the modal and let the user click Copy from Previous inside.
      setShowLogModal(true);
      const prevMonth = result.data.previousReport.reportMonth?.slice(0, 7);
      const label = prevMonth ? new Date(prevMonth + '-01').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'last month';
      toast.success(`Opening assessment — use "Copy from ${label}" inside to pre-fill`, { duration: 4000 });
    } catch (err) {
      toast.error('Failed to check previous data');
    } finally {
      setCopying(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center h-screen ${isMemberPage ? 'bg-black text-white' : 'bg-white'}`}>
        <div className="w-12 h-12 border-4 border-[#AAD816] border-t-transparent rounded-full animate-spin" />
        <p className={`mt-4 ${isMemberPage ? 'text-zinc-500' : 'text-gray-400'} font-bold uppercase tracking-widest text-[10px]`}>Loading Health Intelligence...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${isMemberPage ? 'bg-zinc-950' : 'bg-gray-50'} flex items-center justify-center p-6`}>
        <div className={`${isMemberPage ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-gray-100 text-gray-900'} rounded-3xl shadow-xl max-w-md w-full p-10 text-center space-y-6 border`}>
          <div className={`w-24 h-24 ${isMemberPage ? 'bg-zinc-800 border-zinc-700' : 'bg-gray-50 border-gray-200'} border-2 border-dashed rounded-full flex items-center justify-center mx-auto text-5xl`}>
            🔒
          </div>
          <div className="space-y-2">
            <h3 className={`text-2xl font-black uppercase tracking-tight ${isMemberPage ? 'text-white' : 'text-gray-900'}`}>Access Restricted</h3>
            <p className={`${isMemberPage ? 'text-zinc-500' : 'text-gray-500'} font-medium leading-relaxed`}>{error}</p>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="w-full py-4 bg-[#AAD816] text-black rounded-2xl text-xs font-black uppercase tracking-widest hover:scale-[1.02] transition-all"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const theme = isMemberPage ? 'dark' : 'light';

  return (
    <div className={`min-h-screen ${isMemberPage ? 'bg-black text-white' : 'bg-white text-gray-600'} p-6 lg:p-10 space-y-10 animate-in fade-in duration-700`}>
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-5">
          {memberPhoto ? (
            <div className={`w-16 h-16 rounded-3xl overflow-hidden shadow-xl ${isMemberPage ? 'shadow-[#AAD816]/5 border-zinc-800' : 'shadow-[#AAD816]/20 bg-zinc-100 border-[#AAD816]'} border-2 transform -rotate-3`}>
              <img src={memberPhoto} alt={memberName} className="w-full h-full object-cover transform rotate-3 scale-[1.05]" />
            </div>
          ) : (
            <div className={`w-16 h-16 ${isMemberPage ? 'bg-zinc-900 border border-zinc-800' : 'bg-[#AAD816]'} rounded-3xl shadow-xl shadow-[#AAD816]/20 flex items-center justify-center transform -rotate-6`}>
              <Activity className={`w-8 h-8 ${isMemberPage ? 'text-[#AAD816]' : 'text-black'}`} />
            </div>
          )}
          <div className="space-y-1">
            <h2 className={`text-4xl font-black ${isMemberPage ? 'text-white' : 'text-gray-900'} italic uppercase tracking-tighter`}>
              {memberName}<span className="text-[#AAD816]">.AI</span>
            </h2>
            <div className="flex items-center gap-2">
               <span className={`px-2 py-0.5 ${isMemberPage ? 'bg-zinc-900 text-zinc-500' : 'bg-gray-100 text-gray-400'} text-[9px] font-black rounded uppercase tracking-widest`}>{isMemberPage ? 'Member Intelligence' : 'Coaching Portal'}</span>
               <span className={`w-1 h-1 ${isMemberPage ? 'bg-zinc-800' : 'bg-gray-300'} rounded-full`} />
               <span className={`text-[10px] ${isMemberPage ? 'text-zinc-600' : 'text-gray-400'} font-bold uppercase tracking-widest`}>Biometric Data V2.0</span>
            </div>
          </div>
        </div>
        <div className={`flex items-center gap-3 ${isMemberPage ? 'w-full md:w-auto justify-end px-1' : ''}`}>
          {!isMemberPage && (
            <button 
              onClick={() => navigate('/members')}
              className="px-6 py-4 bg-gray-100 text-gray-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-gray-200 transition-all active:scale-95 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}
          <button 
            onClick={fetchData}
            className={`px-3 py-2.5 ${isMemberPage ? 'bg-zinc-900 text-zinc-400 border border-zinc-800' : 'bg-gray-100 text-gray-500'} rounded-xl hover:scale-105 transition-all active:scale-95 flex items-center justify-center`}
            title="Refresh Data"
          >
            <RotateCw className="w-4 h-4" />
          </button>
          {reports.length > 0 && !isMemberPage && (
            <button
              onClick={handleCopyFromPrevious}
              disabled={copying}
              className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl flex items-center gap-2 transition-all border active:scale-95 disabled:opacity-50 ${
                isMemberPage
                  ? 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-[#AAD816]/50 hover:text-[#AAD816]'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-[#AAD816]/50 hover:text-[#AAD816] shadow-sm'
              }`}
              title="Copy metrics and values from previous month"
            >
              {copying ? (
                <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              Copy from previous
            </button>
          )}
          <button 
            onClick={() => setShowLogModal(true)}
            className={`px-5 py-2.5 bg-[#AAD816] text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] transition-all shadow-lg active:scale-95 flex items-center gap-2 border border-black/5`}
          >
            <ClipboardCheck className="w-4 h-4" />
            {isMemberPage ? 'Manage' : 'Log Assessment'}
          </button>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className={`${isMemberPage ? 'bg-zinc-900/50 border-zinc-800' : 'bg-gray-50 border-gray-200'} rounded-[3rem] p-20 text-center space-y-8 border-2 border-dashed`}>
             <div className={`w-24 h-24 ${isMemberPage ? 'bg-zinc-900 text-zinc-800' : 'bg-white text-gray-300'} rounded-full flex items-center justify-center mx-auto text-4xl shadow-sm italic font-black`}>?</div>
             <div className="space-y-2">
                <h3 className={`text-2xl font-black uppercase italic ${isMemberPage ? 'text-white' : 'text-gray-900'}`}>{isMemberPage ? 'No Intelligence Collected' : 'No Clinical Data Found'}</h3>
                <p className={`${isMemberPage ? 'text-zinc-500' : 'text-gray-500'} max-w-md mx-auto font-medium`}>{isMemberPage ? "You haven't logged any progress metrics yet. Start your journey by logging your first assessment." : "This member hasn't logged any progress metrics yet. Start by logging their first formal assessment."}</p>
             </div>
             <button 
              onClick={() => setShowLogModal(true)}
              className="px-8 py-3 bg-[#AAD816] text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-lg shadow-black/5"
             >
               {isMemberPage ? 'Manage' : 'Initialize First Assessment'}
             </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-10">
          <div className="xl:col-span-2 space-y-12">
            {latestSummary && (
              <MonthlyProgressSummary summary={latestSummary} theme={theme} />
            )}

            {/* Categorized Metrics Grid */}
            {(() => {
                const latestReport = reports[0];
                if (!latestReport) return null;

                const categories: Record<string, typeof latestReport.metrics> = {};
                latestReport.metrics.forEach(m => {
                    const cat = m.metricDefinition.metricCategory;
                    if (!categories[cat]) categories[cat] = [];
                    categories[cat].push(m);
                });

                const catOrder = [
                    'Body Composition', 
                    'Strength', 
                    'Bodyweight Strength', 
                    'Endurance', 
                    'Cardio', 
                    'Flexibility / Mobility', 
                    'Performance Tests'
                ];
                
                const availableCats = Object.keys(categories).sort((a, b) => {
                    const idxA = catOrder.indexOf(a);
                    const idxB = catOrder.indexOf(b);
                    if (idxA === -1 && idxB === -1) return a.localeCompare(b);
                    if (idxA === -1) return 1;
                    if (idxB === -1) return -1;
                    return idxA - idxB;
                });

                return availableCats.map(cat => (
                    <div key={cat} className="space-y-6">
                        <div className="flex items-center gap-3 px-1">
                            <div className="w-1.5 h-6 bg-[#AAD816] rounded-full" />
                            <h3 className={`text-xl font-black italic uppercase tracking-tight ${isMemberPage ? 'text-white' : 'text-gray-900'}`}>
                                {cat.split(' ')[0]} <span className="text-[#AAD816]">{cat.split(' ').slice(1).join(' ')}</span>
                            </h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {categories[cat].map(m => {
                                const mKey = m.metricDefinition.metricKey;
                                const history = reports
                                    .filter(r => r.metrics.some(rm => rm.metricDefinition.metricKey === mKey))
                                    .map(r => ({
                                        metric_date: r.reportMonth,
                                        metric_value: Number(r.metrics.find(rm => rm.metricDefinition.metricKey === mKey)?.metricValue || 0)
                                    }))
                                    .sort((a, b) => new Date(a.metric_date).getTime() - new Date(b.metric_date).getTime());

                                const currentMonth = new Date(latestReport.reportMonth);
                                const lastMonthVal = reports.find(r => {
                                    const d = new Date(r.reportMonth);
                                    return d.getMonth() === (currentMonth.getMonth() === 0 ? 11 : currentMonth.getMonth() - 1) &&
                                           d.getFullYear() === (currentMonth.getMonth() === 0 ? currentMonth.getFullYear() - 1 : currentMonth.getFullYear());
                                })?.metrics.find(rm => rm.metricDefinition.metricKey === mKey)?.metricValue;

                                return (
                                    <MetricRenderer 
                                        key={mKey}
                                        displayType={m.metricDefinition.displayType}
                                        metricData={{
                                            metricKey: mKey,
                                            displayName: m.metricDefinition.displayName,
                                            currentValue: Number(m.metricValue || 0),
                                            previousValue: lastMonthVal !== undefined && lastMonthVal !== null ? Number(lastMonthVal) : null,
                                            unit: m.metricDefinition.metricUnit,
                                            improvementDirection: m.metricDefinition.improvementDirection as 'increase' | 'decrease',
                                            history
                                        }}
                                        theme={theme}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ));
            })()}
          </div>

          <div className="space-y-8">
            {/* Streak card */}
            <div className={`bg-black rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl ${isMemberPage ? 'border border-zinc-800' : ''}`}>
               <div className="absolute top-[-20%] right-[-10%] w-60 h-60 bg-[#AAD816]/10 rounded-full blur-[100px]" />
               <div className="relative z-10 flex flex-col justify-between h-full space-y-6">
                 <div>
                    <p className="text-[10px] text-[#AAD816] font-black uppercase tracking-[0.2em] mb-1">Consistency Streak</p>
                    <div className="flex items-baseline gap-2">
                       <span className="text-7xl font-black italic tracking-tighter">{streak}</span>
                       <span className={`text-sm font-bold ${isMemberPage ? 'text-zinc-500' : 'text-gray-500'} uppercase`}>Months</span>
                    </div>
                 </div>
                 <div className="flex items-center gap-4">
                    <div className={`flex-1 h-2 ${isMemberPage ? 'bg-zinc-800' : 'bg-gray-900'} rounded-full overflow-hidden`}>
                       <div 
                        className="h-full bg-[#AAD816] shadow-[0_0_15px_rgba(170,216,22,0.5)] transition-all duration-1000" 
                        style={{ width: `${Math.min(streak * 10, 100)}%` }}
                       />
                    </div>
                    <span className="text-3xl">🔥</span>
                 </div>
                 <p className={`text-[10px] ${isMemberPage ? 'text-zinc-500' : 'text-gray-400'} font-medium italic`}>{isMemberPage ? "Maintaining your engagement for" : "Maintaining high engagement for"} {streak} consecutive months.</p>
               </div>
            </div>

            <div className={`bg-[#AAD816] rounded-[2rem] p-8 text-black shadow-xl shadow-[#AAD816]/10 group ${isMemberPage ? 'shadow-lime-900/20' : ''}`}>
               <h4 className="text-xs font-black uppercase tracking-widest mb-4">AI Insight</h4>
               <p className="text-xs font-bold leading-relaxed opacity-70">
                 {isMemberPage ? "Based on your last 3 assessments, you are showing significant growth in Bodyweight Strength. Keep pushing!" : "Based on the last 3 assessments, this member is showing significant growth in Bodyweight Strength. Consider increasing intensity in their next periodization phase."}
               </p>
            </div>
          </div>
        </div>
      )}

      <LogStaffProgressModal 
        isOpen={showLogModal} 
        onClose={() => setShowLogModal(false)}
        memberId={memberId!}
        memberName={memberName}
        onSuccess={fetchData}
        mode={isMemberPage ? 'member' : 'gym'}
      />
    </div>
  );
};

export default MemberProgressGymPage;


